"use client";

import Link from "next/link";
import CountryFlag from "@/components/ui/CountryFlag";
import { ComparisonData } from "./types";

interface ComparisonCardProps {
  comparison: ComparisonData;
}

export default function ComparisonCard({ comparison }: ComparisonCardProps) {
  const { slug, title, description, category, country, labelA, labelB } = comparison;

  const countryNames: Record<string, string> = {
    us: "United States",
    in: "India",
    gb: "United Kingdom",
    au: "Australia",
    ca: "Canada",
    sg: "Singapore",
    nz: "New Zealand",
    de: "Germany",
    ch: "Switzerland",
    nl: "Netherlands"
  };

  return (
    <Link href={`/compare/${slug}`} className="comparison-card-link">
      <div className="card comparison-card">
        <div className="card-header-row">
          <span className="category-badge">{category}</span>
          {country && country !== "global" ? (
            <div className="flag-wrapper">
              <CountryFlag code={country} name={countryNames[country.toLowerCase()] || country} className="country-flag-icon" />
              <span className="country-label">{country.toUpperCase()}</span>
            </div>
          ) : (
            <span className="country-label global-label">Universal</span>
          )}
        </div>
        
        <h3 className="card-title">
          {labelA} <span className="vs-text">vs</span> {labelB}
        </h3>
        
        <p className="card-description">{description}</p>
        
        <span className="card-link-action">Compare Now &rarr;</span>
      </div>

      <style jsx>{`
        .comparison-card-link {
          text-decoration: none;
          color: inherit;
        }
        .comparison-card {
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: var(--space-5);
          display: flex;
          flex-direction: column;
          gap: var(--space-3);
          height: 100%;
          transition: transform 0.2s, border-color 0.2s;
        }
        .comparison-card:hover {
          transform: translateY(-2px);
          border-color: var(--border-color-hover);
        }
        .card-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .category-badge {
          background-color: var(--info-light);
          color: var(--info-fg);
          font-size: var(--text-2xs);
          font-weight: var(--fw-bold);
          padding: 3px 8px;
          border-radius: var(--radius-sm);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .flag-wrapper {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .country-flag-icon {
          width: 14px;
          height: 14px;
        }
        .country-label {
          font-family: var(--font-body);
          font-size: var(--text-2xs);
          color: var(--text-muted);
          font-weight: var(--fw-bold);
        }
        .global-label {
          background-color: var(--btn-secondary-hover);
          padding: 2px 6px;
          border-radius: var(--radius-sm);
          color: var(--text-secondary);
        }
        .card-title {
          font-family: var(--font-heading);
          font-size: var(--text-md);
          color: var(--text-primary);
          font-weight: var(--fw-extrabold);
          line-height: 1.2;
        }
        .vs-text {
          color: var(--primary);
          font-style: italic;
          font-size: var(--text-sm);
          margin: 0 2px;
        }
        .card-description {
          font-family: var(--font-body);
          font-size: var(--text-xs);
          color: var(--text-muted);
          line-height: 1.4;
          flex-grow: 1;
        }
        .card-link-action {
          font-family: var(--font-heading);
          font-size: var(--text-sm);
          font-weight: var(--fw-extrabold);
          color: var(--primary);
          display: inline-flex;
          align-items: center;
          margin-top: var(--space-2);
        }
      `}</style>
    </Link>
  );
}
